import { Box } from '@mui/material';
import { gridStringOrNumberComparator } from '@mui/x-data-grid-pro';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { PopperContent } from '@/components/poppers/PopperContent';
import { HeaderCellTypography } from '@/components/tables/components/HeaderCellTypography';
import { ROW_BORDER_STYLE } from '@/components/tables/constants';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { Column } from '@/components/tables/DataTable/types';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import {
  AssetForTaxSitus,
  IntegratedAssetTaxSitusCell,
} from '@/modules/entities/details/shared/IntegratedAssetTaxSitusCell';
import { useUpdateIntegratedAssetTaxSitus } from '@/modules/entities/details/shared/IntegratedEntityAssetsCard.utils';
import {
  StructuredAssetsSubform_AssetFragment,
  StructuredAssetsSubform_ValuationFragment,
} from '@/modules/entities/StructuredAssetsSubform/graphql/StructuredAssetsSubform.generated';
import { STATE_TAX_SITUS_HELP_BODY } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.fields';
import { AssetTaxSitusFormValue } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import { EntityType } from '@/modules/entities/types/EntityType';
import { AssetValuationV2ValuationSource } from '@/types/schema';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';

import { getIntegratedAssetsFromValuation } from './FullScreenStructuredAssetsModal.utils';
import { useValuationIntegrationDetails } from './hooks/useValuationLinkDetails';

export interface AssetData {
  id: string;
  assetCategory: string;
  title: string;
  value: Decimal;
  asset?: StructuredAssetsSubform_AssetFragment;
}

interface UseValuationSummaryColumnsOptions {
  onSitusChange?: (
    asset: AssetForTaxSitus,
    nextSitus: AssetTaxSitusFormValue
  ) => void | Promise<void>;
  ownerPrincipalIds?: string[];
}

export function useValuationSummaryColumns({
  onSitusChange,
  ownerPrincipalIds,
}: UseValuationSummaryColumnsOptions = {}): Column<AssetData>[] {
  return useMemo(() => {
    const baseColumns: Column<AssetData>[] = [
      { field: 'title', headerName: 'Name', flex: 2 },
      { field: 'assetCategory', headerName: 'Asset category', flex: 1 },
    ];

    if (onSitusChange) {
      baseColumns.push({
        field: 'asset',
        headerName: 'State',
        width: 180,
        sortable: false,
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5 }}>
            <HeaderCellTypography>State</HeaderCellTypography>
            <ContextualHelpTooltip>
              <PopperContent body={STATE_TAX_SITUS_HELP_BODY} />
            </ContextualHelpTooltip>
          </Box>
        ),
        renderCell: ({ row }) =>
          row.asset ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                px: '12px',
                py: '8px',
                borderTop: ROW_BORDER_STYLE,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <IntegratedAssetTaxSitusCell
                  asset={row.asset}
                  onChange={onSitusChange}
                  ownerPrincipalIds={ownerPrincipalIds}
                />
              </Box>
            </Box>
          ) : null,
      });
    }

    baseColumns.push({
      field: 'value',
      headerName: 'Value',
      flex: 1,
      renderCell: TextRenderer({
        text: ({ row }) => formatCurrencyNoDecimalsAccounting(row.value),
      }),
      sortComparator: (
        v1: AssetData['value'],
        v2: AssetData['value'],
        p1,
        p2
      ) => {
        return gridStringOrNumberComparator(v1, v2, p1, p2);
      },
    });

    return baseColumns;
  }, [onSitusChange, ownerPrincipalIds]);
}

function getRows(
  valuation: StructuredAssetsSubform_ValuationFragment | null
): AssetData[] {
  if (!valuation) return [];
  // only show assets from an integration; the manual assets are shown in the form.
  const assets = getIntegratedAssetsFromValuation(valuation);
  return assets.map((asset) => ({
    id: asset.id,
    assetCategory: asset.class?.displayName ?? 'Unknown',
    title: asset.displayName,
    value: asset.assetValue?.ownedValue ?? new Decimal(0),
    asset,
  }));
}

interface IntegrationValuationSummaryProps {
  valuation: StructuredAssetsSubform_ValuationFragment | null;
  entityId: string;
  entityType: EntityType;
  subtypeId: string;
  designerAccountId: string | null | undefined;
  ownerPrincipalIds: string[];
}

export const MAX_ASSET_COUNT = 20;
export function IntegrationValuationSummary({
  valuation,
  entityId,
  entityType,
  subtypeId,
  designerAccountId,
  ownerPrincipalIds,
}: IntegrationValuationSummaryProps) {
  const { isIntegratedScenarioDirty } = useValuationIntegrationDetails();

  const { handleChange: handleSitusChange } = useUpdateIntegratedAssetTaxSitus({
    entityId,
    entityType,
    subtypeId,
    designerAccountId: designerAccountId ?? '',
    valuationId: valuation?.id ?? '',
    source: 'structured_assets_modal_inline',
  });
  const onSitusChange =
    designerAccountId && valuation?.id ? handleSitusChange : undefined;

  const columns = useValuationSummaryColumns({
    onSitusChange,
    ownerPrincipalIds,
  });

  const hasValidIntegrationValuation =
    valuation &&
    valuation.valuationSource !== AssetValuationV2ValuationSource.Manual &&
    !isIntegratedScenarioDirty;

  // Don't render manual valuation details as if they are linked to an integration
  const rows = hasValidIntegrationValuation ? getRows(valuation) : [];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      slots={{
        noRowsOverlay: () => (
          <TableEmptyState
            text={
              hasValidIntegrationValuation
                ? 'No assets found in this valuation.'
                : 'Detailed assets will appear after saving and replace any existing data.'
            }
          />
        ),
      }}
      pageSize={MAX_ASSET_COUNT}
      pagination={rows.length > MAX_ASSET_COUNT}
      autoHeight
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableDensitySelector
      disableRowSelectionOnClick
    />
  );
}
